/**
 * Utilitários para integração com WhatsApp
 */

export interface OrderData {
  orderCode: string;
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  paymentMethod: string;
  installments?: number;
  items: Array<{
    name: string;
    quantity: number;
    price: number;
    color?: string | null;
    size?: string | null;
  }>;
  subtotal: number;
  shippingCost: number;
  total: number;
}

/**
 * Formatar mensagem do pedido para WhatsApp
 */
export function formatarMensagemPedido(orderData: OrderData, whatsappNumber: string): string {
  const { orderCode, customerName, customerPhone, customerAddress, paymentMethod, installments, items, subtotal, shippingCost, total } = orderData;

  // Formatar forma de pagamento
  let formaPagamento = '';
  switch (paymentMethod) {
    case 'pix':
      formaPagamento = 'PIX';
      break;
    case 'credit':
      formaPagamento = `Cartão de Crédito${installments && installments > 1 ? ` (${installments}x sem juros)` : ''}`;
      break;
    case 'boleto':
      formaPagamento = 'Boleto';
      break;
    default:
      formaPagamento = paymentMethod;
  }

  // Montar lista de itens
  const itensTexto = items.map((item, index) => {
    let itemTexto = `${index + 1}. ${item.name} - Qtd: ${item.quantity} - R$ ${item.price.toFixed(2).replace('.', ',')}`;
    if (item.color) {
      itemTexto += ` - Cor: ${item.color}`;
    }
    if (item.size) {
      itemTexto += ` - Tamanho: ${item.size}`;
    }
    return itemTexto;
  }).join('\n');

  // Montar mensagem completa
  const mensagem = `🛒 *NOVO PEDIDO - ${orderCode}*

👤 *Cliente:*
${customerName}
📞 ${customerPhone}

📍 *Endereço de Entrega:*
${customerAddress}

🛍️ *Itens do Pedido:*
${itensTexto}

💰 *Resumo:*
Subtotal: R$ ${subtotal.toFixed(2).replace('.', ',')}
Frete: ${shippingCost === 0 ? 'Grátis' : `R$ ${shippingCost.toFixed(2).replace('.', ',')}`}
*Total: R$ ${total.toFixed(2).replace('.', ',')}*

💳 *Forma de Pagamento:*
${formaPagamento}

---
Pedido criado em ${new Date().toLocaleString('pt-BR')}`;

  return mensagem;
}

/**
 * Abrir WhatsApp com mensagem formatada
 */
export function enviarMensagemWhatsApp(orderData: OrderData, whatsappNumber: string): void {
  const mensagem = formatarMensagemPedido(orderData, whatsappNumber);
  
  // Remover caracteres especiais do número (apenas números)
  const numeroLimpo = whatsappNumber.replace(/\D/g, '');
  
  // Codificar mensagem para URL
  const mensagemEncoded = encodeURIComponent(mensagem);
  
  // Criar URL do WhatsApp
  const whatsappUrl = `https://wa.me/${numeroLimpo}?text=${mensagemEncoded}`;
  
  // Abrir em nova aba
  window.open(whatsappUrl, '_blank');
}

/**
 * Obter número do WhatsApp da configuração
 * Pode vir do backend ou de variável de ambiente
 */
export async function obterNumeroWhatsApp(): Promise<string> {
  try {
    // Tentar buscar do backend primeiro usando getApiEndpoint
    const { getApiEndpoint } = await import('./api-url');
    const response = await fetch(getApiEndpoint('/config'));
    
    if (response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const config = await response.json();
        // O backend retorna config.site.whatsapp
        if (config.site?.whatsapp) {
          return config.site.whatsapp;
        }
        // Fallback: verificar se está no nível raiz
        if (config.whatsapp) {
          return config.whatsapp;
        }
      }
    }
  } catch (error) {
    console.warn('Não foi possível buscar número do WhatsApp do backend:', error);
  }
  
  // Fallback: usar variável de ambiente ou valor padrão
  return import.meta.env.VITE_WHATSAPP_NUMBER || '5511999999999';
}

